import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Plus, RefreshCw, Search, X } from 'lucide-react';
import {
  actOnCooloff,
  listCooloff,
  listCooloffEvents,
  type CooloffAction,
  type CooloffEventAction,
  type CooloffItem,
  type CooloffState,
} from '../lib/api/cooloff.js';
import { listEntities } from '../lib/api/entities.js';
import type { EntitySummary } from '../lib/api/types.js';
import { handleMutationConflict } from '../lib/mutationConflict.js';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '../components/ui/modal.js';
import { TableScroll } from '../components/ui/table-scroll.js';
import { Button } from '../components/ui/button.js';

const LIST_KEY = ['cooloff'] as const;

const STATE_LABEL: Record<CooloffState, string> = {
  active: 'Active',
  paused: 'Paused',
  expired: 'Expired',
};

const STATE_CHIP: Record<CooloffState, string> = {
  active: 'bg-danger-100 text-danger-700 border-danger-200',
  paused: 'bg-warning-100 text-warning-800 border-warning-200',
  expired: 'bg-grey-100 text-grey-600 border-grey-200',
};

const EVENT_LABEL: Record<CooloffEventAction, string> = {
  add: 'Added',
  remove: 'Removed (paused)',
  readd: 'Re-added',
  reset: 'Reset',
  expire: 'Expired',
};

/** What one action does, as the confirm dialog states it (design §2). */
interface PendingAction {
  item: { entity_id: string; entity_name: string; ticker: string | null };
  action: CooloffAction;
  title: string;
  effect: string;
  reasonRequired: boolean;
}

function days(n: number | null): string {
  if (n === null) return '—';
  const d = Math.max(0, Math.ceil(n));
  return `${d} d`;
}

function errorText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return (e as Error)?.message ?? 'failed';
}

/**
 * Entity cool-off (CS511 lever 3, CS512): entities that declined paid coverage,
 * weighted against on every masthead with a 45-day half-life until they expire
 * at 180 days. Sales add, remove (pause), re-add and reset them here.
 */
export function CooloffPage() {
  const [showExpired, setShowExpired] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [adding, setAdding] = useState(false);

  const list = useQuery({ queryKey: LIST_KEY, queryFn: listCooloff });
  const fullPenalty = list.data?.full_penalty ?? -30;
  const halfLife = list.data?.half_life_days ?? 45;
  const expiry = list.data?.expiry_days ?? 180;
  const items = (list.data?.items ?? []).filter((i) => showExpired || i.state !== 'expired');

  const open = (item: CooloffItem, action: CooloffAction) => {
    if (action === 'remove') {
      setPending({
        item,
        action,
        title: 'Remove from cool-off',
        effect: `The penalty stops now. The clock pauses at day ${Math.floor(item.elapsed_days)}, and re-adding resumes it from there.`,
        reasonRequired: false,
      });
    } else if (action === 'reset') {
      setPending({
        item,
        action,
        title: 'Reset cool-off',
        effect: `The clock goes back to day 0: full weight (${fullPenalty}) and a fresh ${expiry} days.`,
        reasonRequired: false,
      });
    } else {
      setPending({
        item,
        action,
        title: item.state === 'paused' ? 'Re-add to cool-off' : 'Add to cool-off again',
        effect:
          item.state === 'paused'
            ? `The clock resumes at day ${Math.floor(item.elapsed_days)} (weight ${item.weight_if_active.toFixed(1)}).`
            : `A fresh cool-off: full weight (${fullPenalty}) for ${expiry} days.`,
        reasonRequired: true,
      });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-grey-900">Cool-off</h1>
          <p className="text-sm text-grey-600 mt-1 max-w-3xl">
            Entities we approached that declined paid coverage. While active, each one&apos;s filings score{' '}
            {fullPenalty} merit on every masthead, halving every {halfLife} days, and the cool-off ends after{' '}
            {expiry} days. Substance-5 news, coverage customers and Bell Wire&apos;s mandated events are never
            penalised. Removing pauses the clock; re-adding resumes it.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            onClick={() => list.refetch()}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
          >
            <RefreshCw className={`h-4 w-4 ${list.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <Button onClick={() => setAdding(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>
      </header>

      <label className="mb-3 inline-flex items-center gap-2 text-sm text-grey-700">
        <input type="checkbox" checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} />
        Show expired
      </label>

      {list.isLoading ? (
        <p className="text-sm text-grey-500">Loading…</p>
      ) : list.isError ? (
        <p className="p-4 bg-danger-50 border border-danger-200 text-danger-700 rounded text-sm">
          Cool-off list unavailable: {errorText(list.error)}
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-grey-500">No entities in cool-off.</p>
      ) : (
        <TableScroll>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-grey-500 border-b border-grey-200">
                <th className="py-2 pr-2 w-6" />
                <th className="py-2 pr-4">Entity</th>
                <th className="py-2 pr-4">State</th>
                <th className="py-2 pr-4">Weight</th>
                <th className="py-2 pr-4">Next halving</th>
                <th className="py-2 pr-4">Expires</th>
                <th className="py-2 pr-4">Reason</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <Fragment key={item.entity_id}>
                  <tr className="border-b border-grey-100 align-top">
                    <td className="py-2 pr-2">
                      <button
                        onClick={() => setExpanded(expanded === item.entity_id ? null : item.entity_id)}
                        className="text-grey-400 hover:text-grey-700"
                        aria-label="History"
                      >
                        {expanded === item.entity_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="py-2 pr-4">
                      <div className="font-medium text-grey-900">{item.entity_name}</div>
                      {item.ticker && <div className="font-mono text-xs text-grey-500">{item.ticker}</div>}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${STATE_CHIP[item.state]}`}>
                        {STATE_LABEL[item.state]}
                      </span>
                      <div className="text-xs text-grey-500 mt-1">day {Math.floor(item.elapsed_days)}</div>
                    </td>
                    <td className="py-2 pr-4">
                      <WeightBar
                        weight={item.state === 'paused' ? item.weight_if_active : item.weight}
                        full={fullPenalty}
                        muted={item.state !== 'active'}
                      />
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {item.state === 'active' ? `in ${days(item.next_halving_in_days)}` : '—'}
                    </td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {item.state === 'expired' ? 'expired' : `in ${days(item.expires_in_days)}`}
                      {item.state === 'paused' && <div className="text-xs text-grey-500">clock paused</div>}
                    </td>
                    <td className="py-2 pr-4 max-w-xs">
                      <div className="text-grey-800">{item.reason || '—'}</div>
                      <div className="text-xs text-grey-500">
                        {item.updated_by ?? 'unknown'}
                        {item.updated_at && ` · ${new Date(item.updated_at).toLocaleDateString()}`}
                      </div>
                    </td>
                    <td className="py-2 whitespace-nowrap text-right space-x-2">
                      {item.state === 'active' && (
                        <>
                          <Button variant="outline" onClick={() => open(item, 'remove')}>Remove</Button>
                          <Button variant="outline" onClick={() => open(item, 'reset')}>Reset</Button>
                        </>
                      )}
                      {item.state !== 'active' && (
                        <Button variant="outline" onClick={() => open(item, 'add')}>Re-add</Button>
                      )}
                    </td>
                  </tr>
                  {expanded === item.entity_id && (
                    <tr className="border-b border-grey-100 bg-grey-50">
                      <td />
                      <td colSpan={7} className="py-3 pr-4">
                        <History entityId={item.entity_id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {adding && (
        <AddDialog
          fullPenalty={fullPenalty}
          expiry={expiry}
          onClose={() => setAdding(false)}
          onPick={(entity) => {
            setAdding(false);
            setPending({
              item: { entity_id: entity.entity_id, entity_name: entity.display_name, ticker: entity.canonical_identifier ?? null },
              action: 'add',
              title: 'Add to cool-off',
              effect: `Full weight (${fullPenalty}) on every masthead, halving every ${halfLife} days, ending after ${expiry} days.`,
              reasonRequired: true,
            });
          }}
        />
      )}
      {pending && <ConfirmDialog pending={pending} onClose={() => setPending(null)} />}
    </div>
  );
}

/** −30 → full bar, 0 → empty. */
function WeightBar({ weight, full, muted }: { weight: number; full: number; muted: boolean }) {
  const pct = full === 0 ? 0 : Math.min(100, Math.max(0, (weight / full) * 100));
  return (
    <div className="w-32">
      <div className="h-2 rounded bg-grey-200 overflow-hidden">
        <div className={`h-2 ${muted ? 'bg-grey-400' : 'bg-danger-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-grey-600 mt-1 tabular-nums">
        {weight.toFixed(1)} of {full}
        {muted && weight !== 0 && ' (kept)'}
      </div>
    </div>
  );
}

function History({ entityId }: { entityId: string }) {
  const events = useQuery({
    queryKey: ['cooloff-events', entityId],
    queryFn: () => listCooloffEvents(entityId),
  });
  if (events.isLoading) return <p className="text-xs text-grey-500">Loading history…</p>;
  if (events.isError) {
    return <p className="text-xs text-danger-700">History unavailable: {errorText(events.error)}</p>;
  }
  if (!events.data?.length) return <p className="text-xs text-grey-500">No events recorded.</p>;
  return (
    <ul className="space-y-1 text-xs text-grey-700">
      {events.data.map((ev, i) => (
        <li key={`${ev.at}-${i}`}>
          <span className="tabular-nums text-grey-500">{new Date(ev.at).toLocaleString()}</span>{' '}
          <span className="font-semibold">{EVENT_LABEL[ev.action]}</span>
          {ev.elapsed_days_before !== null && <span className="text-grey-500"> at day {Math.floor(ev.elapsed_days_before)}</span>}
          {' · '}
          {ev.actor ?? 'system'}
          {ev.reason && <span className="text-grey-600">: {ev.reason}</span>}
        </li>
      ))}
    </ul>
  );
}

function AddDialog({
  fullPenalty,
  expiry,
  onClose,
  onPick,
}: {
  fullPenalty: number;
  expiry: number;
  onClose: () => void;
  onPick: (entity: EntitySummary) => void;
}) {
  const [search, setSearch] = useState('');
  const results = useQuery({
    queryKey: ['entity-search', search],
    queryFn: () => listEntities({ search: search.trim(), page_size: 10 }),
    enabled: search.trim().length >= 2,
  });
  return (
    <Modal onClose={onClose} size="lg">
      <ModalHeader>
        <h2 className="text-xl font-bold text-grey-900">Add to cool-off</h2>
        <button onClick={onClose} className="text-grey-400 hover:text-grey-600 touch-target" aria-label="Close">
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>
      <ModalBody>
        <p className="text-sm text-grey-600 mb-3">
          Search by ticker or name. The entity starts at {fullPenalty} for {expiry} days.
        </p>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-grey-400" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ticker or company name"
            className="w-full pl-9 pr-3 py-2 border border-grey-300 rounded bg-surface text-grey-900"
          />
        </div>
        {results.isError ? (
          <p className="text-sm text-danger-700">Search unavailable: {errorText(results.error)}</p>
        ) : results.isFetching ? (
          <p className="text-sm text-grey-500">Searching…</p>
        ) : search.trim().length >= 2 && results.data?.items.length === 0 ? (
          <p className="text-sm text-grey-500">No matching entity.</p>
        ) : (
          <ul className="divide-y divide-grey-100">
            {results.data?.items.map((e) => (
              <li key={e.entity_id}>
                <button onClick={() => onPick(e)} className="w-full text-left px-2 py-2 hover:bg-grey-50">
                  <span className="font-medium text-grey-900">{e.display_name}</span>
                  {e.canonical_identifier && (
                    <span className="ml-2 font-mono text-xs text-grey-500">{e.canonical_identifier}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </ModalBody>
    </Modal>
  );
}

function ConfirmDialog({ pending, onClose }: { pending: PendingAction; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => actOnCooloff(pending.item.entity_id, pending.action, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ['cooloff-events', pending.item.entity_id] });
      onClose();
    },
    onError: (e) => {
      if (handleMutationConflict(e, qc, [LIST_KEY])) {
        onClose();
        return;
      }
      setError(errorText(e));
    },
  });
  const blocked = pending.reasonRequired && !reason.trim();
  return (
    <Modal onClose={onClose} canClose={!mutation.isPending} size="md">
      <ModalHeader>
        <h2 className="text-xl font-bold text-grey-900">{pending.title}</h2>
      </ModalHeader>
      <ModalBody>
        <p className="font-medium text-grey-900">
          {pending.item.entity_name}
          {pending.item.ticker && <span className="ml-2 font-mono text-sm text-grey-500">{pending.item.ticker}</span>}
        </p>
        <p className="text-sm text-grey-600 mt-2 mb-4">{pending.effect}</p>
        <label className="block text-sm text-grey-700 mb-1">
          Reason{pending.reasonRequired ? '' : ' (optional)'}
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-grey-300 rounded bg-surface text-grey-900"
          placeholder={pending.action === 'add' ? 'e.g. declined Coverage tier, call on 31 Oct' : ''}
        />
        {error && <p className="mt-3 text-sm text-danger-700">{error}</p>}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button onClick={() => mutation.mutate()} disabled={blocked || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Confirm'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
